import SkTechApiClient from "./base/SkTechApiClient";
import { ConfigApi } from "./base/ConfigApi";

export interface Currency {
    id: number;
    name: string;
    symbol: string;
    shortName: string;
    companyId: number;
    isMain: boolean;
}

export interface OperationCategory {
    id: number;
    name: string;
}

export interface OperationType {
    id: number;
    name: string;
    description: string;
    operationCategoryId: number;
    operationCategory?: OperationCategory;
    companyId: number;
}

export interface PaymentType {
    id: number;
    name: string;
    companyId: number;
}

export interface FinancialTransaction {
    id: number;
    name: string;
    description: string;
    amount: number;
    date: string;
    operationTypeId: number;
    operationType?: OperationType;
    paymentTypeId: number;
    paymentType?: PaymentType;
    currencyId: number;
    currency?: Currency;
    companyId: number;
    createdById?: number;
}

export interface PaginatedResult<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

/** Matches `SkTech.Server.EntityModels.Finances.ErpEntityType`; the string names are what `ErpHistoryController`'s
 * `Enum.TryParse` route segment expects. */
export type ErpEntityType = 'FinancialTransaction' | 'Currency' | 'OperationType' | 'OperationCategory';

export interface ErpHistoryEntry {
    version: number;
    action: 'Created' | 'Updated' | 'Deleted';
    userId?: number;
    userName?: string;
    createdAt: string;
    changes?: { field: string; oldValue?: string; newValue?: string }[];
    snapshotJson?: string;
}

export interface ErpAttachment {
    id: number;
    financialTransactionId: number;
    fileName: string;
    mimeType?: string;
    sizeBytes: number;
    url: string;
    resourceType: string;
    uploadedAt: string;
    uploadedByUserId?: number;
    uploadedByName?: string;
}

/** Wire shape of `GET api/ErpHistory/{entityType}/{entityId}`, i.e. the raw `ErpChangeLog` entity (Newtonsoft
 * camelCase). `action` is `ErpChangeAction`'s *int* value (1=Created, 2=Updated, 3=Deleted): the server has no
 * StringEnumConverter registered, and EF's `HasConversion<int>()` only governs DB storage, not JSON output. */
interface ErpChangeLogDto {
    version: number;
    action: number;
    userId?: number | null;
    userName?: string | null;
    createdAt: string;
    changesJson?: string | null;
    snapshotJson?: string | null;
}

/** `changesJson` is filled server-side via plain `System.Text.Json.JsonSerializer.Serialize(changes)` (no camelCase
 * policy applied), so — unlike the outer Newtonsoft-serialized response — its keys are PascalCase. */
interface ErpFieldChangeWire {
    Field?: string;
    OldValue?: string | null;
    NewValue?: string | null;
}

const ERP_CHANGE_ACTIONS: Record<number, ErpHistoryEntry['action']> = { 1: 'Created', 2: 'Updated', 3: 'Deleted' };

function parseErpChanges(changesJson?: string | null): ErpHistoryEntry['changes'] {
    if (!changesJson) return undefined;
    try {
        const parsed = JSON.parse(changesJson);
        if (!Array.isArray(parsed)) return undefined;
        return (parsed as ErpFieldChangeWire[]).map(c => ({
            field: c.Field ?? '',
            oldValue: c.OldValue ?? undefined,
            newValue: c.NewValue ?? undefined,
        }));
    } catch {
        return undefined;
    }
}

function toErpHistoryEntry(dto: ErpChangeLogDto): ErpHistoryEntry {
    return {
        version: dto.version,
        action: ERP_CHANGE_ACTIONS[dto.action] ?? 'Updated',
        userId: dto.userId ?? undefined,
        userName: dto.userName ?? undefined,
        createdAt: dto.createdAt,
        changes: parseErpChanges(dto.changesJson),
        snapshotJson: dto.snapshotJson ?? undefined,
    };
}

/** Wire shape of `ErpAttachment` rows returned by `ErpAttachmentController` (Newtonsoft camelCase). */
interface ErpAttachmentDto {
    id: number;
    financialTransactionId: number;
    fileName: string;
    mimeType?: string | null;
    sizeBytes: number;
    secureUrl: string;
    resourceType: string;
    uploadedAt: string;
    uploadedByUserId?: number | null;
    uploadedByName?: string | null;
}

function toErpAttachment(dto: ErpAttachmentDto): ErpAttachment {
    return {
        id: dto.id,
        financialTransactionId: dto.financialTransactionId,
        fileName: dto.fileName,
        mimeType: dto.mimeType ?? undefined,
        sizeBytes: dto.sizeBytes,
        url: dto.secureUrl,
        resourceType: dto.resourceType,
        uploadedAt: dto.uploadedAt,
        uploadedByUserId: dto.uploadedByUserId ?? undefined,
        uploadedByName: dto.uploadedByName ?? undefined,
    };
}

export class CurrencyApiClient extends SkTechApiClient {
    constructor(c: ConfigApi) { super(c); }
    public async getAll() { return this.Get('/api/Currency'); }
    public async getById(id: number) { return this.Get(`/api/Currency/${id}`); }
    public async add(cur: Partial<Currency>) { return this.Post('/api/Currency', cur); }
    public async update(id: number, cur: Partial<Currency>) { return this.Put(`/api/Currency/${id}`, cur); }
    public async delete(id: number) { return this.Delete(`/api/Currency/${id}`); }
}

export class OperationTypeApiClient extends SkTechApiClient {
    constructor(c: ConfigApi) { super(c); }
    public async getAll() { return this.Get('/api/OperationType'); }
    public async getById(id: number) { return this.Get(`/api/OperationType/${id}`); }
    public async getCategories() { return this.Get('/api/OperationType/categories'); }
    public async add(o: Partial<OperationType>) { return this.Post('/api/OperationType', o); }
    public async update(id: number, o: Partial<OperationType>) { return this.Put(`/api/OperationType/${id}`, o); }
    public async delete(id: number) { return this.Delete(`/api/OperationType/${id}`); }
}

export class PaymentTypeApiClient extends SkTechApiClient {
    constructor(c: ConfigApi) { super(c); }
    public async getAll() { return this.Get('/api/PaymentType'); }
    public async getById(id: number) { return this.Get(`/api/PaymentType/${id}`); }
    public async add(p: Partial<PaymentType>) { return this.Post('/api/PaymentType', p); }
    public async update(id: number, p: Partial<PaymentType>) { return this.Put(`/api/PaymentType/${id}`, p); }
    public async delete(id: number) { return this.Delete(`/api/PaymentType/${id}`); }
}

export class FinancialTransactionApiClient extends SkTechApiClient {
    constructor(c: ConfigApi) { super(c); }
    
    // Original methods
    public async getAll() { return this.Get('/api/FinancialTransaction'); }
    public async getById(id: number) { return this.Get(`/api/FinancialTransaction/${id}`); }
    public async getByDateRange(s: string, e: string) { 
        return this.Get(`/api/FinancialTransaction/date-range?startDate=${s}&endDate=${e}`); 
    }
    public async add(t: Partial<FinancialTransaction>) { return this.Post('/api/FinancialTransaction', t); }
    public async update(id: number, t: Partial<FinancialTransaction>) { 
        return this.Put(`/api/FinancialTransaction/${id}`, t); 
    }
    public async delete(id: number) { return this.Delete(`/api/FinancialTransaction/${id}`); }
    public async getVersions(id: number) { return this.Get(`/api/FinancialTransaction/${id}/versions`); }
    public async rollback(id: number, vId: number) { 
        return this.Post(`/api/FinancialTransaction/${id}/rollback/${vId}`, {}); 
    }
    
    // New pagination method with direct query string passing
    public async getPaginated(queryString: string) { 
        console.log('API Request:', `/api/FinancialTransaction/paginated?${queryString}`);
        return this.Get(`/api/FinancialTransaction/paginated?${queryString}`); 
    }
    
    // Method for exporting all filtered transactions
    public async getForExport(queryString: string) {
        return this.Get(`/api/FinancialTransaction/export?${queryString}`);
    }

    public async getSummary(queryString: string) {
        console.log('API Request (summary):', `/api/FinancialTransaction/summary?${queryString}`);
        return this.Get(`/api/FinancialTransaction/summary?${queryString}`);
      }

    // ERP audit history — GET api/ErpHistory/{entityType}/{entityId}; covers this and the other three ERP entities.
    public async getHistory(entityType: ErpEntityType, entityId: number): Promise<ErpHistoryEntry[]> {
        const r = await this.Get(`/api/ErpHistory/${entityType}/${entityId}`);
        return ((r?.data ?? []) as ErpChangeLogDto[]).map(toErpHistoryEntry);
    }

    // Operation (FinancialTransaction) attachments — api/ErpAttachment/*.
    public async getAttachments(transactionId: number): Promise<ErpAttachment[]> {
        const r = await this.Get(`/api/ErpAttachment/${transactionId}`);
        return ((r?.data ?? []) as ErpAttachmentDto[]).map(toErpAttachment);
    }

    public async uploadAttachment(transactionId: number, file: File): Promise<ErpAttachment> {
        const formData = new FormData();
        formData.append('file', file);
        // 409 FileAlreadyExists / 400 ParentNotFound / 502 UploadFailed reject with the AxiosError (not swallowed
        // here) so the UI can read error.response.data.code.
        const r = await this.Post(`/api/ErpAttachment/${transactionId}`, formData);
        return toErpAttachment(r.data as ErpAttachmentDto);
    }

    public async deleteAttachment(id: number): Promise<void> {
        await this.Delete(`/api/ErpAttachment/${id}`);
    }

    // No trailing slash normalization needed: BASE_URL never ends with one (see getApiBaseUrl).
    public downloadAttachmentUrl(id: number): string {
        return `${this.BASE_URL}/api/ErpAttachment/${id}/download`;
    }
}